import { MatrixClient } from "matrix-js-sdk/src/client";
import { type Room, type RoomMember } from "matrix-js-sdk/src/matrix";
import { _t } from "../languageHandler";
import { IDthContact } from "./clientHttp/models/httpClientModels";
import { findContactByDisplayName } from "./clientHttp/utils/utils";
import { ENVIRONMENT, M_DIRECT_EVENT_CACHE_KEY } from "./constants";
import TimeoutError from "./clientHttp/errors/TimeoutError";
import DMRoomMap from "../utils/DMRoomMap";
import { ContactStateManager } from "../customisations/ContactStateManager";

//#region Date Utils
/**
 * Ottieni il timestamp corrente in millisecondi.
 * @returns Il timestamp corrente in millisecondi.
 */
export function getCurrentTimestamp(): number {
    return new Date().getTime();
}

/**
 * Converte un oggetto Date in un timestamp in millisecondi.
 * @param date L'oggetto Date da convertire.
 * @returns Il timestamp in millisecondi.
 */
export function dateToTimestamp(date: Date): number {
    return date.getTime();
}

/**
 * Calcola i millisecondi rimanenti fino a una data di scadenza.
 * @param expirationTimestampMillis Il timestamp di scadenza in millisecondi.
 * @returns I millisecondi rimanenti fino alla scadenza.
 */
export function getMillisUntilExpiration(expirationTimestampMillis: number | undefined | null): number {
    if (!expirationTimestampMillis) {
        return 0;
    }
    const now = getCurrentTimestamp();
    const diff = expirationTimestampMillis - now;
    return Math.max(0, diff);
}

/**
 * Calcola i secondi rimanenti fino a una data di scadenza.
 * @param expirationTimestampSeconds Il timestamp di scadenza in secondi.
 * @returns I secondi rimanenti fino alla scadenza.
 */
export function getSecondsUntilExpiration(expirationTimestampSeconds: number | undefined | null): number {
    const expirationTimestampMillis = expirationTimestampSeconds ? expirationTimestampSeconds * 1000 : undefined;
    const millisUntilExpiration = getMillisUntilExpiration(expirationTimestampMillis);
    const secondsUntilExpiration = Math.floor(millisUntilExpiration / 1000);
    return secondsUntilExpiration;
}

/**
 * Converte un timestamp (millisecondi) in un oggetto Date.
 * @param timestamp Il timestamp in millisecondi.
 * @returns Un oggetto Date rappresentante il timestamp.
 */
export function timestampToDate(timestamp: number): Date {
    return new Date(timestamp);
}

/**
 * Converte una stringa di timestamp in un numero (timestamp in millisecondi)
 * @param timestampStr La stringa che rappresenta un timestamp (es. "1699999999999")
 * @returns Il timestamp come numero
 */
export function stringTimestampToNumber(timestampStr: string): number {
    const parsed = Number(timestampStr);
    if (isNaN(parsed)) {
        throw new Error(_t("error.invalid_timestamp"));
    }
    return parsed;
}

/**
 * Converte una stringa di data (in formato ISO o altro) in un oggetto Date.
 * @param dateString La stringa che rappresenta una data.
 * @returns Un oggetto Date.
 */
export function stringToDate(dateString: string): Date {
    return new Date(dateString);
}

/**
 * Converte una stringa di data (in formato ISO o altro) in un timestamp in millisecondi.
 * @param dateString La stringa che rappresenta una data.
 * @returns Il timestamp in millisecondi.
 */
export function stringToTimestamp(dateString: string): number {
    return new Date(dateString).getTime();
}

/**
 * Converte un oggetto Date in una stringa di formato ISO (o altro formato se necessario).
 * @param date L'oggetto Date da convertire.
 * @param format Il formato in cui restituire la data (opzionale, default: ISO).
 * @returns La data come stringa.
 */
export function dateToString(date: Date, format: string = "ISO"): string {
    if (format === "ISO") {
        return date.toISOString(); // Restituisce una stringa in formato ISO
    }
    // Altri formati possono essere aggiunti qui in base alle esigenze
    return date.toLocaleString(); // Default: formato locale
}

/**
 * Converte un timestamp in millisecondi in una stringa di formato ISO.
 * @param timestamp Il timestamp in millisecondi.
 * @returns La data come stringa ISO.
 */
export function timestampToString(timestamp: number): string {
    return new Date(timestamp).toISOString();
}

/**
 * Converte un timestamp in millisecondi in una stringa di formato locale.
 * @param timestamp Il timestamp in millisecondi.
 * @returns La data come stringa nel formato locale.
 */
export function timestampToLocaleString(timestamp: number): string {
    return new Date(timestamp).toLocaleString();
}

/**
 * Aggiungi un intervallo di tempo (in millisecondi) alla data.
 * @param date La data iniziale.
 * @param milliseconds L'intervallo da aggiungere in millisecondi.
 * @returns Una nuova data con l'intervallo aggiunto.
 */
export function addMillisecondsToDate(date: Date, milliseconds: number): Date {
    date.setTime(date.getTime() + milliseconds);
    return date;
}

/**
 * Aggiungi un intervallo di tempo (in giorni) alla data.
 * @param date La data iniziale.
 * @param days L'intervallo da aggiungere in giorni.
 * @returns Una nuova data con l'intervallo aggiunto.
 */
export function addDaysToDate(date: Date, days: number): Date {
    date.setDate(date.getDate() + days);
    return date;
}

/**
 * Calcola la differenza in millisecondi tra due date.
 * @param startDate La data di inizio.
 * @param endDate La data di fine.
 * @returns La differenza in millisecondi.
 */
export function getDateDifferenceInMilliseconds(startDate: Date, endDate: Date): number {
    return endDate.getTime() - startDate.getTime();
}

/**
 * Calcola la differenza in giorni tra due date.
 * @param startDate La data di inizio.
 * @param endDate La data di fine.
 * @returns La differenza in giorni.
 */
export function getDateDifferenceInDays(startDate: Date, endDate: Date): number {
    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    return Math.floor(getDateDifferenceInMilliseconds(startDate, endDate) / millisecondsPerDay);
}

/**
 * Controlla se una data è nel futuro rispetto alla data corrente.
 * @param date La data da verificare.
 * @returns True se la data è nel futuro, false altrimenti.
 */
export function isDateInTheFuture(date: Date): boolean {
    return date.getTime() > getCurrentTimestamp();
}

/**
 * Controlla se una data è nel passato rispetto alla data corrente.
 * @param date La data da verificare.
 * @returns True se la data è nel passato, false altrimenti.
 */
export function isDateInThePast(date: Date): boolean {
    return date.getTime() < getCurrentTimestamp();
}

//#endregion

//#region STRING UTILS
export function isNullOrWhitespace(input: string | null | undefined): boolean {
    return !input || !input.trim();
}

export function getShortLanguageCode(language: string) {
    if (!language) {
        return '';  // Se la lingua non è definita, restituisce una stringa vuota.
    }

    // Esegui il split della lingua alla prima occorrenza di "-" e prendi la parte prima del trattino.
    const shortCode = language.split('-')[0].toLowerCase();

    return shortCode;
}

export { getContactFormatOrDefault, formatDisplayName, getContactNameFormat } from "./contactFormatUtils";

/**
 * Capitalizza la prima lettera di ogni parola in una stringa.
 * @param str La stringa da modificare.
 * @returns La stringa con la prima lettera di ogni parola capitalizzata.
 */
export function toCamelCase(str: string): string {
    if (!str) return str;
    // Capitalizza ogni parola, inclusi caratteri accentati
    return str.replace(
        // /\b[a-zà-öø-ÿ]/gi, // \b NON è Unicode-aware
        /(^|[\s\-'])\p{L}/gu, // SUPPORTO COMPLETO PER CARATTERI UNICODE (es. lettere accentate)
        c => c.toUpperCase());
}

//#endregion

//#region CIFRATURA
const encoder = new TextEncoder();
const decoder = new TextDecoder();

/**
 * Converte una stringa segreta in una CryptoKey per AES-GCM
 */
async function getCryptoKey(secret: string): Promise<CryptoKey> {
    const keyData = encoder.encode(secret.padEnd(32).slice(0, 32)); // AES-256 => 32 byte
    return crypto.subtle.importKey("raw", keyData, { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);
}

/**
 * Cifra un testo con una chiave segreta.
 * Ritorna una stringa base64 contenente IV + dati cifrati.
 */
export async function encryptText(plainText: string, secret: string): Promise<string> {
    const cryptoKey = await getCryptoKey(secret);
    const iv = crypto.getRandomValues(new Uint8Array(12)); // IV = 12 byte

    const encrypted = await crypto.subtle.encrypt(
        { name: "AES-GCM", iv },
        cryptoKey,
        encoder.encode(plainText)
    );

    // Concatena IV + dati cifrati in un unico array
    const combined = new Uint8Array(iv.length + encrypted.byteLength);
    combined.set(iv, 0);
    combined.set(new Uint8Array(encrypted), iv.length);

    // Restituisce tutto in base64
    return btoa(String.fromCharCode(...combined));
}

/**
 * Decifra un testo cifrato con la stessa chiave usata per cifrarlo.
 * Input: stringa base64 contenente IV + dati cifrati.
 */
export async function decryptText(encryptedBase64: string, secret: string): Promise<string | null> {
    try {
        const combined = Uint8Array.from(atob(encryptedBase64), c => c.charCodeAt(0));

        const iv = combined.slice(0, 12); // primi 12 byte = IV
        const data = combined.slice(12);  // resto = dati cifrati

        const cryptoKey = await getCryptoKey(secret);

        const decrypted = await crypto.subtle.decrypt(
            { name: "AES-GCM", iv },
            cryptoKey,
            data
        );

        return decoder.decode(decrypted);
    } catch (err) {
        console.error("Errore durante la decifratura:", err);
        return null;
    }
}

//#endregion

//#region OBJECT Utils
export function isNullOrEmptyObject(obj: unknown): boolean {
    return obj == null || (typeof obj === "object" && Object.keys(obj).length === 0);
}

/**
 * Controlla se un oggetto ha una proprietà specifica, evitando errori se l'oggetto è null o non è un oggetto.
 * @param obj L'oggetto da controllare
 * @param prop La proprietà da verificare
 * @returns True se l'oggetto ha la proprietà, false altrimenti
 */
export function hasProperty(obj: unknown, prop: string): boolean {
    return obj != null && typeof obj === "object" && Object.prototype.hasOwnProperty.call(obj, prop);
}

/**
 * Esegue un deep clone di un oggetto, utilizzando structuredClone se disponibile, altrimenti un fallback con JSON.
 * Nota: il fallback con JSON non supporta funzioni, simboli, o oggetti complessi come Map/Set.
 * @param obj L'oggetto da clonare
 * @returns Un nuovo oggetto che è una copia profonda dell'originale.
 */
export function deepCloneObject<O extends object>(obj: O): O {
    let objClone: any;
    if (typeof structuredClone === "function") {
        objClone = structuredClone(obj);
    } else {
        objClone = JSON.parse(JSON.stringify(obj));
    }
    return objClone;
}
//#endregion

//#region Configuration Utils
export enum ProductEnum {
    VVF = "vvf-connect",
    WICIR = "wicir",
    GOT = "got"
}

export enum EnvironmentEnum {
    Development = "dev",
    Production = "prod",
}

/**
 * Determina l'ambiente di esecuzione (sviluppo o produzione) in base alla variabile di ambiente NODE_ENV,
 * restituendo un valore dell'enum EnvironmentEnum.
 * @returns EnvironmentEnum.Development se NODE_ENV inizia con "dev" (case-insensitive), altrimenti EnvironmentEnum.Production.
 */
export function getCurrentEnvironment(): EnvironmentEnum {
    const env = ENVIRONMENT.toLowerCase();
    return getEnvironmentByValue(env);
}

/**
 * Determina l'ambiente di esecuzione (sviluppo o produzione) in base a una stringa di input.
 * @param value La stringa da analizzare, tipicamente ottenuta da una variabile di configurazione o ambiente.
 * @returns EnvironmentEnum.Development se la stringa inizia con "dev" (case-insensitive), altrimenti EnvironmentEnum.Production.
 */
export function getEnvironmentByValue(value?: string): EnvironmentEnum {
    if (value?.toLowerCase().startsWith("dev")) {
        return EnvironmentEnum.Development;
    }
    return EnvironmentEnum.Production;
}
//#endregion

//#region Concurrency Utils
/**
 * Esegue una promessa con un timeout. 
 * Se la promessa non si risolve entro il tempo specificato, 
 * viene rigettata con un errore di timeout.
 * @param promise La promessa da eseguire
 * @param ms Il tempo massimo in millisecondi da attendere
 * @param timeoutMessage Il messaggio dell'errore in caso di timeout
 * @returns La promessa originale se risolta entro il tempo, altrimenti rigetta con un errore di timeout
 */
export function promiseWithTimeout<T>(promise: Promise<T>, ms: number, timeoutMessage = "Timeout"): Promise<T> {
    return Promise.race([
        promise,
        new Promise<T>((resolve, reject) => setTimeout(() => reject(new TimeoutError(timeoutMessage)), ms)),
    ]);
}
//#endregion 

//#region MATRIX UTILS

/**
 * Trova la prima room associata a un matrix name, confrontando sia "Nome Cognome" che "Cognome Nome".
 * @param matrixClient Il client di Matrix da utilizzare per cercare le room
 * @param matrixName Il matrix name da cercare
 * @returns La room associata al matrix name, o null se non trovata
 */
export function findRoomByMatrixName(matrixClient: MatrixClient, matrixName: string): Room | null {
    const roomIds: string[] = findRoomIdsByMatrixName(matrixName);
    // Se trovi una o più room associate a quel matrix name, 
    // restituisci la prima che trovi 
    // (in caso di più room, si parte dall'ultima creata, che presumibilmente è quella più rilevante)
    for (let i = roomIds.length - 1; i >= 0; i--) {
        const roomId = roomIds[i];
        // Prova a recuperare la room dal client di Matrix usando l'ID della room
        const room: Room | null = matrixClient.getRoom(roomId);
        // Se la room esiste, la restituisce
        if (room) return room;
    }
    // Se non trova nessuna room associata a quel matrix name, restituisce null
    return null;
}

/**
 * Trova la room associata a un display name, confrontando sia "Nome Cognome" che "Cognome Nome".
 * @param displayName Display name da cercare
 * @returns La room associata al display name, o null se non trovata
 */
export function findRoomByDisplayName(matrixClient: MatrixClient, displayName: string): Room | null {
    let room: Room | null = null;
    try {
        // Usa ContactStateManager come fonte di verità per i contatti (con lazy load da session storage)
        const contacts: IDthContact[] = ContactStateManager.getContacts();
        const matrixName = displayName ?
            findContactByDisplayName(contacts, displayName)?.matrixName
            : undefined;
        // Se trovi un contatto con quel matrix name, 
        // prova a trovare una room di chat associata a quel contatto
        if (matrixName) {
            room = findRoomByMatrixName(matrixClient, matrixName);
        }
    } catch (e) {
        console.error("Errore durante la ricerca della room associata alla chiamata in arrivo:", e);
    }
    return room;
}

/**
 * Trova gli ID delle room associate a un matrix name
 * @param matrixName Matrix name da cercare
 * @returns ID delle room associate al matrix name, o array vuoto se non trovato
 */
export function findRoomIdsByMatrixName(matrixName: string): string[] {
    let roomIds: string[] = [];
    try {
        // Se trovi un contatto con quel matrix name, 
        // prova a trovare una room di chat associata a quel contatto
        if (matrixName) {
            const directsMapJson = localStorage.getItem(M_DIRECT_EVENT_CACHE_KEY)
            if (directsMapJson) {
                const directsMap = JSON.parse(directsMapJson) as Record<string, string[]>;
                // Prende tutte le room associate a quel contatto
                const directRooms = directsMap[matrixName];
                if (directRooms && directRooms.length > 0) {
                    roomIds = [...directRooms];
                }
            }
        }
    } catch (e) {
        console.error("Errore durante la ricerca della room associata alla chiamata in arrivo:", e);
    }
    return roomIds;
}

/**
 * Restituisce il membro della room che rappresenta l'altro partecipante in una chat 1:1, 
 * o null se non è una chat 1:1 o se il membro non è trovato.
 * @param room La room da cui ottenere il membro
 * @returns Il membro della room che rappresenta l'altro partecipante, o null se non trovato
 */
export function getDmMemberId(room: Room): string | null {
    return DMRoomMap.shared().getUserIdForRoomId(room.roomId) || null;
}

/**
 * Restituisce il membro della room che rappresenta l'altro partecipante in una chat 1:1, 
 * o null se non è una chat 1:1 o se il membro non è trovato.
 * @param room La room da cui ottenere il membro
 * @returns Il membro della room che rappresenta l'altro partecipante, o null se non trovato
 */
export function getDmMember(room: Room): RoomMember | null {
    const otherUserId = getDmMemberId(room);
    return otherUserId ? room.getMember(otherUserId) : null;
}

/**
 * Controlla se una room è una chat 1:1 (DM) verificando se esiste un membro associato all'altro partecipante.
 * @param room La room da verificare
 * @returns True se la room è una chat 1:1, false altrimenti
 */
export function isDmRoom(room: Room): boolean {
    return !!getDmMemberId(room);
}
//#endregion

//#region Browser Utils
/**
 * Controlla se l'app ha il permesso di accedere al microfono, utilizzando le Permissions API del browser.
 * @returns Lo stato del permesso per il microfono: 'granted', 'denied' o 'prompt'. 
 * Se le Permissions API non sono supportate, restituisce 'prompt' come default.
 */
async function checkGenericPermissions(name: PermissionName): Promise<PermissionState> {
    try {
        const result = await navigator.permissions.query({ name });
        return result.state; // 'granted' | 'denied' | 'prompt'
    } catch (error) {
        console.error('Permissions API non supportata', error);
        return 'prompt';
    }
}

/**
 * Controlla se l'app ha il permesso di accedere al microfono, utilizzando le Permissions API del browser.
 * @returns Lo stato del permesso per il microfono: 'granted', 'denied' o 'prompt'. 
 * Se le Permissions API non sono supportate, restituisce 'prompt' come default.
 */
async function checkMicrophonePermissions(): Promise<PermissionState> {
    return checkGenericPermissions('microphone');
}

/**
 * Controlla se l'app ha il permesso di accedere al microfono.
 * @returns True se il permesso è stato concesso, false altrimenti.
 */
export async function checkMicrophonePermissionsGranted(): Promise<boolean> {
    const permission = await checkMicrophonePermissions();
    return permission === 'granted';
}

//#endregion